import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { customDebounce } from "../../../utilities/debounce";

import {
  Avatar,
  BasicDataGrid,
  Chip,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  DataGridRef,
} from "@inductiveautomation/ws-components";
import PublicProfileDrawer from "./PublicProfile.Drawer";
import { usePublicProfileActions } from "./PublicProfile.actions";
import {
  GetProfileApiResponse,
  usePutIsEnabledMutation,
} from "../api/profile.api";
import { useUpdatePublicProfileFields } from "../hooks/useUpdatePublicProfileFields";

interface PublicProfileProps {
  publicProfile: GetProfileApiResponse["publicProfile"];
}

const PublicProfile = ({ publicProfile }: PublicProfileProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "profile.public_profile",
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dataGridRef = useRef<DataGridRef>(null);
  const [putIsEnabled] = usePutIsEnabledMutation();
  const [enabled, setEnabled] = useState(publicProfile.isEnabled);
  const url = `${window.location.origin}/${publicProfile.publicId}`;
  const { updatePublicProfileShareField } = useUpdatePublicProfileFields();
  const updateIsEnabled = async (boolean: boolean) => {
    setEnabled(boolean);
    return putIsEnabled({ isEnabledPayload: { isEnabled: boolean } });
  };
  const actionLists = usePublicProfileActions(
    enabled,
    customDebounce(updateIsEnabled),
    url
  );

  return (
    <>
      <BasicDataGrid
        ref={dataGridRef}
        testId="profile-public-profile"
        enableSorting={false}
        headings={[
          {
            id: "heading-profile",
            label: t("headings.profile"),
          },
          {
            id: "heading-status",
            label: "",
          },
        ]}
        onSelection={(rowId) => setDrawerOpen(!!rowId)}
        data={[
          {
            id: "profile-public-profile-card",
            testId: "profile-public-profile-card",
            contentColumns: [
              createDataCardInitialColumnDefinition({
                parentTestId: "profile-public-profile-card",
                avatar: (
                  <Avatar icon={"person-public"} iconAvatarColor="primary" />
                ),
                heading: t("card.public_profile"),
                subtext: url,
              }),
              createInfoCardColumnDefinition(
                <Chip
                  testId="profile-public-profile-chip-container"
                  variant="status"
                  state="neutral"
                  items={[
                    {
                      id: "profile-public-profile-chip",
                      label: enabled
                        ? t("card.statuses.enabled.label")
                        : t("card.statuses.disabled.label"),
                    },
                  ]}
                />,
                96,
                100
              ),
            ],
            actionLists,
            childItems: [],
          },
        ]}
        labels={{
          singular_item_name: t("card.public_profile"),
          plural_item_name: t("card.public_profiles"),
        }}
      />

      <PublicProfileDrawer
        enabled={enabled}
        setEnabled={customDebounce(updateIsEnabled)}
        url={url}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          dataGridRef.current?.clearCheckedAndSelected();
        }}
        publicProfile={publicProfile}
        handleFieldUpdate={updatePublicProfileShareField}
      />
    </>
  );
};

export default PublicProfile;
